'use es6';

import PropTypes from 'prop-types';
import { Component } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import I18n from 'I18n';
import classNames from 'classnames';
import styled from 'styled-components';
import SyntheticEvent from 'UIComponents/core/SyntheticEvent';
import Controllable from 'UIComponents/decorators/Controllable';
import UIButton from 'UIComponents/button/UIButton';
import UILink from 'UIComponents/link/UILink';
import UIFormLabel from 'UIComponents/form/UIFormLabel';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIPopoverBody from 'UIComponents/tooltip/UIPopoverBody';
import UITabs from 'UIComponents/nav/UITabs';
import UITab from 'UIComponents/nav/UITab';
import createLazyPropType from 'UIComponents/utils/propTypes/createLazyPropType';
import lazyEval from 'UIComponents/utils/lazyEval';
import ColorPicker from '../ColorPicker';
import { ColorPickerModes, DefaultColorGrid } from '../constants';
import SquatchGrid from './SquatchGrid';
import { isValidFavorites } from '../utils/validateColor';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FavoritesLabelContainer = styled.div.withConfig({
  displayName: "ColorPickerPopoverBody__FavoritesLabelContainer",
  componentId: "cjv469-0"
})(["margin-bottom:2px;display:flex;justify-content:space-between;"]);
const ResetButton = styled(UIButton).withConfig({
  displayName: "ColorPickerPopoverBody__ResetButton",
  componentId: "cjv469-1"
})(["width:100%;"]);
class ColorPickerPopoverBody extends Component {
  constructor(...args) {
    super(...args);
    this.handleResetClick = () => {
      const {
        defaultAlpha,
        defaultColor,
        onChange,
        onResetClick,
        onOpenChange
      } = this.props;
      if (onResetClick) {
        onResetClick();
      } else {
        onChange({
          color: defaultColor,
          alpha: defaultAlpha
        });
      }

      // Always close the picker on reset
      if (onOpenChange) {
        onOpenChange(SyntheticEvent(false));
      }
    };
    this.handleColorChange = ({
      color,
      alpha
    }) => {
      const {
        onChange,
        onOpenChange,
        pickerMode
      } = this.props;
      onChange({
        color,
        alpha
      });

      // Close the picker when on the SIMPLE tab
      if (pickerMode === ColorPickerModes.SIMPLE && onOpenChange) {
        onOpenChange(SyntheticEvent(false));
      }
    };
  }
  renderFavoritesLink() {
    const {
      editFavoritesLink,
      showFavoritesLink
    } = this.props;
    if (!editFavoritesLink || !showFavoritesLink) {
      return null;
    }
    return /*#__PURE__*/_jsx(UILink, {
      external: true,
      href: editFavoritesLink,
      children: I18n.text('colorPicker.popover.edit')
    });
  }
  renderFavorites() {
    const {
      color,
      favorites,
      editFavoritesLabel
    } = this.props;
    if (!isValidFavorites(favorites)) {
      return null;
    }
    return /*#__PURE__*/_jsxs("div", {
      "data-test-id": "colorpicker-favorites",
      className: "private-react-colorpicker-popover--favorites m-top-2",
      children: [/*#__PURE__*/_jsxs(FavoritesLabelContainer, {
        children: [/*#__PURE__*/_jsx(UIFormLabel, {
          className: "p-y-0",
          children: lazyEval(editFavoritesLabel)
        }), this.renderFavoritesLink()]
      }), /*#__PURE__*/_jsx(SquatchGrid, {
        currentColor: color,
        grid: favorites,
        onClick: this.handleColorChange
      })]
    });
  }
  renderReset() {
    const {
      showResetToDefault
    } = this.props;
    if (!showResetToDefault) {
      return null;
    }
    return /*#__PURE__*/_jsx(UIFlex, {
      children: /*#__PURE__*/_jsx(ResetButton, {
        "data-test-id": "colorpicker-reset-button",
        className: "private-react-colorpicker-popover--reset m-top-3",
        onClick: this.handleResetClick,
        size: "small",
        use: "tertiary-light",
        children: I18n.text('colorPicker.popover.reset')
      })
    });
  }
  renderSimpleTab() {
    const {
      color,
      colorGrid,
      favorites
    } = this.props;
    const isFavoriteColor = color && isValidFavorites(favorites) && favorites.indexOf(color.toLowerCase()) >= 0;
    return /*#__PURE__*/_jsxs(UITab, {
      onMouseDown: evt => evt.preventDefault(),
      tabId: ColorPickerModes.SIMPLE,
      title: I18n.text('colorPicker.popover.mode.simple'),
      children: [/*#__PURE__*/_jsx(SquatchGrid, {
        currentColor: color,
        grid: colorGrid,
        onClick: this.handleColorChange,
        showMatchedSquatch: !isFavoriteColor
      }), this.renderFavorites(), this.renderReset()]
    });
  }
  renderAdvancedTab() {
    const {
      alpha,
      alphaClassName,
      boardClassName,
      color,
      colorsClassName,
      colorPickerClassName,
      includeAlpha,
      ribbonClassName
    } = this.props;
    const advancedClasses = classNames(colorPickerClassName, 'p-all-0');
    return /*#__PURE__*/_jsxs(UITab, {
      onMouseDown: evt => evt.preventDefault(),
      tabId: ColorPickerModes.ADVANCED,
      title: I18n.text('colorPicker.popover.mode.advanced'),
      children: [/*#__PURE__*/_jsx(ColorPicker, {
        alpha: alpha,
        alphaClassName: alphaClassName,
        boardClassName: boardClassName,
        className: advancedClasses,
        color: color,
        colorsClassName: colorsClassName,
        fieldsetSize: "small",
        hexOnly: false,
        includeAlpha: includeAlpha,
        onChange: this.handleColorChange,
        ribbonClassName: ribbonClassName
      }), this.renderReset()]
    });
  }
  render() {
    const {
      className,
      onPickerModeChange,
      pickerMode
    } = this.props;
    return /*#__PURE__*/_jsx(UIPopoverBody, {
      flush: true,
      className: className,
      children: /*#__PURE__*/_jsxs(UITabs, {
        fill: true,
        onSelectedChange: onPickerModeChange,
        panelClassName: "p-all-4 m-all-0",
        selected: pickerMode,
        use: "toolbar",
        children: [this.renderSimpleTab(), this.renderAdvancedTab()]
      })
    });
  }
}
ColorPickerPopoverBody.propTypes = {
  alpha: PropTypes.number,
  alphaClassName: PropTypes.string,
  boardClassName: PropTypes.string,
  className: PropTypes.string,
  color: PropTypes.string,
  colorGrid: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape({
    color: PropTypes.string,
    alpha: PropTypes.string
  })])),
  colorPickerClassName: PropTypes.string,
  colorsClassName: PropTypes.string,
  defaultAlpha: PropTypes.number,
  defaultColor: PropTypes.string,
  editFavoritesLink: PropTypes.string,
  editFavoritesLabel: createLazyPropType(PropTypes.string),
  favorites: PropTypes.arrayOf(PropTypes.string),
  includeAlpha: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onPickerModeChange: PropTypes.func,
  onOpenChange: PropTypes.func,
  onResetClick: PropTypes.func,
  pickerMode: PropTypes.oneOf(Object.keys(ColorPickerModes).map(key => ColorPickerModes[key])),
  ribbonClassName: PropTypes.string,
  showFavoritesLink: PropTypes.bool,
  showResetToDefault: PropTypes.bool
};
ColorPickerPopoverBody.defaultProps = {
  colorGrid: DefaultColorGrid,
  defaultAlpha: 100,
  defaultColor: '#FFFFFF',
  includeAlpha: false,
  pickerMode: ColorPickerModes.SIMPLE,
  onPickerModeChange: emptyFunction.thatReturnsNull,
  showResetToDefault: true,
  editFavoritesLabel: () => I18n.text('colorPicker.favorites')
};
export default Controllable(ColorPickerPopoverBody, ['pickerMode']);